import React, { useEffect, useState } from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { PrimaryHeader, PrimaryHeaderDataProps } from './primaryHeader/PrimaryHeader';
import { SecondaryHeader, SecondaryHeaderDataProps } from './secondaryHeader/SecondaryHeader';
import { AnimationDirection, AnimationDirectionType } from './menuAnimations.util';
import { MEDIA_BREAKPOINTS } from '../../../../mediaQueries.const';
import { MontiAnchor } from './MontiAnchor';
import { ErrorBoundary } from '../../../errorHandling/ErrorBoundary';

const animationDuration = 200;

const getStyle = () => {
  return StyleSheet.create({
    header: {
      position: 'fixed',
      width: '100%',
      zIndex: 1500,
      top: 0,
      transform: 'translate3d(0, 0, 0)',
      transition: `${animationDuration}ms`,
    },
    foldingUp: {
      [MEDIA_BREAKPOINTS.medium]: {
        transform: 'translateY(-100%)',
        transitionDelay: `${animationDuration}ms`,
      },
      [MEDIA_BREAKPOINTS.small]: {
        transform: 'translateY(-100%)',
        transitionDelay: `${animationDuration}ms`,
      },
    },
    foldingDown: {
      [MEDIA_BREAKPOINTS.medium]: {
        transform: 'translateY(0)',
      },
      [MEDIA_BREAKPOINTS.small]: {
        transform: 'translateY(0)',
      },
    },
  });
};

export interface HeaderLinksDataProps {
  href: string;
  text: string;
}

export interface HeaderDataProps {
  primaryHeader: PrimaryHeaderDataProps;
  secondaryHeader: SecondaryHeaderDataProps | null;
}

export const Header: React.FunctionComponent<HeaderDataProps> = props => {
  const firstMenuFoldThreshold = 640;
  const secondMenuFoldThreshold = 100;

  const [scrollTicking, setScrollTicking] = useState(false);
  const [menuFirstFolded, setMenuFirstFolded] = useState(false);
  const [menuFoldState, setMenuFoldState] = useState(AnimationDirection.NONE);

  const isScrollingUp = (deltaY: number) => {
    return deltaY < 0;
  };

  const isScrollingDown = (deltaY: number) => {
    return deltaY > 0;
  };

  const getMenuAnimation = (originalScrollPosition: number): AnimationDirectionType => {
    const currentScrollPosition = window.scrollY;
    const deltaY = currentScrollPosition - originalScrollPosition;

    if (currentScrollPosition > firstMenuFoldThreshold && !menuFirstFolded) {
      setMenuFirstFolded(true);
      return AnimationDirection.UP;
    }

    if (isScrollingUp(deltaY)) {
      return AnimationDirection.DOWN;
    }

    if (isScrollingDown(deltaY) && menuFirstFolded && currentScrollPosition > secondMenuFoldThreshold) {
      return AnimationDirection.UP;
    }

    return menuFoldState;
  };

  const scrollHandler = () => {
    if (!scrollTicking) {
      const originalY = window.scrollY;
      setScrollTicking(true);
      setTimeout(() => {
        const menuAnimation = getMenuAnimation(originalY);
        if (menuAnimation !== menuFoldState) {
          setMenuFoldState(menuAnimation);
        }
        setScrollTicking(false);
      }, 100);
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', scrollHandler);

    return () => window.removeEventListener('scroll', scrollHandler);
  });

  const generateSecondaryHeader = (secondaryHeader: SecondaryHeaderDataProps) => {
    if (!secondaryHeader) {
      return null;
    }
    return (
      <SecondaryHeader
        moreLinksText={secondaryHeader.moreLinksText}
        links={secondaryHeader.links}
        logo={secondaryHeader.logo}
        animation={menuFoldState}
        animationDuration={animationDuration}
        pagePath={secondaryHeader.pagePath}
      />
    );
  };

  const getHeaderStyleWithAnimation = () => {
    const style = getStyle();

    if (menuFoldState === AnimationDirection.NONE) {
      return css(style.header);
    }

    const animationStyle = menuFoldState === AnimationDirection.UP ? style.foldingUp : style.foldingDown;
    return css(style.header, animationStyle);
  };


  const { primaryHeader, secondaryHeader } = props;
  const { links, logo, editions, cta, menu, videoLogo, pagePath, editionEndpoint } = primaryHeader;

  return (
    <ErrorBoundary>
      <MontiAnchor isSecondaryHeaderEnabled={!!secondaryHeader} menuAnimation={menuFoldState} />
      <header className={getHeaderStyleWithAnimation()}>
        <PrimaryHeader {...{ links, logo, editions, cta, menu, videoLogo, pagePath, editionEndpoint }} />
        {generateSecondaryHeader(secondaryHeader as SecondaryHeaderDataProps)}
      </header>
    </ErrorBoundary>
  );
};
